import { type HistoryGroup } from "@/features/history/utils";
import { useModals } from "@/features/modals/hooks";
import styled from "@emotion/styled";
import {
  type History,
  HistoryEntryTypeEnum,
  deleteHistoryEntry,
  updateHistoryEntry,
  useProfile,
} from "@repo/api";
import {
  Button,
  FormField,
  Hyperlink,
  Text,
  TextArea,
  Variables,
  noteIcon,
  pencilIcon,
  pinFilledIcon,
  pinIcon,
  trashCanIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { FlexDiv, ListViewItemWithActions, useToast } from "@repo/ui";
import { getErrorMessage, trackAnalytics } from "@repo/utils";
import { useState } from "react";

import { NoteItemHistoryInfo } from "./NoteItemHistoryInfo";
import { MAX_NOTE_LENGTH, MIN_NOTE_LENGTH } from "./noteUtils";

const PreWrapText = styled(Text)`
  white-space: pre-wrap;
`;

const CompactFormField = styled(FormField)`
  && {
    margin-bottom: 0px;
  }
`;

interface NoteItemProps {
  note: History | HistoryGroup;
}

export const NoteItem = ({ note }: NoteItemProps) => {
  const noteText = note.note ?? "";
  const TRUNCATE_AT_LINES = 2;
  const TRUNCATE_AT_CHARS = 220;
  const lines = noteText.split("\n");
  const isLongByLines = lines.length > TRUNCATE_AT_LINES;
  const isLongByChars = noteText.length > TRUNCATE_AT_CHARS;
  const isLong = isLongByLines || isLongByChars;
  const truncatedText = isLongByLines
    ? lines.slice(0, TRUNCATE_AT_LINES).join("\n")
    : noteText.slice(0, TRUNCATE_AT_CHARS);
  const [expanded, setExpanded] = useState(false);
  const [isPinning, setIsPinning] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(noteText);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: profile } = useProfile();
  const { updateToast } = useToast();
  const { pushModal } = useModals();
  const isMutableNote = note.entryType === HistoryEntryTypeEnum.CUSTOM_NOTES;
  const isDeleteAllowed =
    isMutableNote &&
    profile?.mail?.toLowerCase() === note.createdBy?.toLowerCase();
  const trimmedDraft = draft.trim();
  const isDraftValid =
    trimmedDraft.length >= MIN_NOTE_LENGTH &&
    trimmedDraft.length <= MAX_NOTE_LENGTH;

  const handleTogglePin = () => {
    if (!isMutableNote) {
      return;
    }

    setIsPinning(true);
    updateHistoryEntry(note.opportunityId, note.id, {
      entryType: note.entryType,
      isPinned: !note.isPinned,
    }).finally(() => setIsPinning(false));
  };

  const handleStartEdit = () => {
    if (!isMutableNote) {
      return;
    }

    setDraft(noteText);
    setIsEditing(true);
    trackAnalytics("edit_custom_note");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setDraft(noteText);
  };

  const handleSaveEdit = () => {
    if (!isMutableNote) {
      return;
    }

    if (!isDraftValid || trimmedDraft === noteText.trim()) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    updateHistoryEntry(note.opportunityId, note.id, {
      entryType: note.entryType,
      note: trimmedDraft,
      updatedBy: profile?.mail ?? "",
    })
      .then(() => {
        setIsEditing(false);
        updateToast({ title: t("prompt.note_updated") });
      })
      .catch((err) => {
        updateToast({
          title: t("prompt.note_update_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      })
      .finally(() => setIsSaving(false));
  };

  const handleDelete = async () => {
    if (!isDeleteAllowed) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteHistoryEntry(note.opportunityId, note.id, {
        entryType: HistoryEntryTypeEnum.CUSTOM_NOTES,
      });
      updateToast({ title: t("prompt.note_deleted") });
      trackAnalytics("delete_custom_note");
    } catch (err) {
      updateToast({
        title: t("prompt.note_deleted_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmDelete = () => {
    pushModal({
      type: "CONFIRM",
      onConfirm: handleDelete,
      title: t("action.delete_note"),
      message: t("prompt.confirm_delete_note"),
    });
  };

  if (isEditing) {
    return (
      <ListViewItemWithActions
        inset
        icon={noteIcon}
        title={
          <FlexDiv flexDirection="column" gap={Variables.space75} width="100%">
            <CompactFormField
              fieldHelper={{
                id: `edit-note-helper-${note.id}`,
                msg: `${draft.length}/${MAX_NOTE_LENGTH}`,
              }}
            >
              <TextArea
                id={`edit-note-input-${note.id}`}
                label={t("label.write_a_note")}
                maxLength={MAX_NOTE_LENGTH}
                value={draft}
                autoFocus
                onChange={(e) => setDraft(e.target.value)}
              />
            </CompactFormField>
            <FlexDiv justifyContent="end" gap={Variables.space75}>
              <Button
                size="xsmall"
                type="tertiary"
                text={t("action.cancel")}
                onClick={handleCancelEdit}
              />
              <Button
                size="xsmall"
                type="primary"
                text={t("action.save")}
                loading={isSaving}
                disabled={!isDraftValid}
                onClick={handleSaveEdit}
              />
            </FlexDiv>
          </FlexDiv>
        }
      />
    );
  }

  return (
    <ListViewItemWithActions
      inset
      icon={noteIcon}
      disabled={isDeleting}
      title={
        isLong ? (
          <>
            <PreWrapText textStyle="Body.M">
              {expanded ? noteText : `${truncatedText}...`}
            </PreWrapText>
            <Hyperlink
              button
              text={expanded ? t("action.show_less") : t("action.show_more")}
              onClick={(e) => {
                e.preventDefault();
                setExpanded((prev) => !prev);
              }}
            />
          </>
        ) : (
          <PreWrapText textStyle="Body.M">{noteText}</PreWrapText>
        )
      }
      addOn={
        <>
          <NoteItemHistoryInfo
            createdBy={note.createdBy}
            createdAt={note.createdAt}
          />
          {note.updatedAt && (
            <NoteItemHistoryInfo
              createdBy={note.updatedBy}
              createdAt={note.updatedAt}
              label="label.edited_by"
            />
          )}
        </>
      }
      action={
        isMutableNote ? (
          <>
            <Button
              size="small"
              type="tertiary"
              iconOnly
              ssrIcon={note.isPinned ? pinFilledIcon : pinIcon}
              text={note.isPinned ? t("action.unpin") : t("action.pin")}
              title={note.isPinned ? t("action.unpin") : t("action.pin")}
              loading={isPinning}
              onClick={handleTogglePin}
            />
            <Button
              size="small"
              type="tertiary"
              iconOnly
              ssrIcon={pencilIcon}
              text={t("action.edit")}
              title={t("action.edit")}
              onClick={handleStartEdit}
            />
            {isDeleteAllowed && (
              <Button
                size="small"
                type="tertiary"
                iconOnly
                ssrIcon={trashCanIcon}
                text={t("action.delete")}
                title={t("action.delete")}
                loading={isDeleting}
                onClick={handleConfirmDelete}
              />
            )}
          </>
        ) : undefined
      }
    />
  );
};
